<template>
    <div class="energy-flow" :class="cardSize">
        <svg :viewBox="layout.viewBox" preserveAspectRatio="xMidYMid meet">
            <!-- 匯流排：所有來源與去向都掛在這條線上 -->
            <path class="bus-bar" :d="`M ${BUS.left} ${BUS.y} L ${BUS.right} ${BUS.y}`" />

            <!-- 連線先畫，節點後畫，節點才會蓋在線頭上 -->
            <path
                v-for="link in links"
                :key="link.id"
                :d="link.d"
                :class="['link', link.state]"
                :style="{ strokeWidth: link.width, stroke: link.color }"
            />

            <g v-for="node in nodes" :key="node.id" :class="['node', node.state]">
                <circle :cx="node.x" :cy="node.y" :r="NODE_RADIUS" :style="{ fill: node.color }" />
                <text :x="node.x" :y="node.y + 9" class="node-icon">{{ node.icon }}</text>
                <!-- 名稱與數值併成一行：卡片區高度有限，兩行會逼得整張圖縮到極小 -->
                <text :x="node.x" :y="node.y + NODE_RADIUS + 22" class="node-label">
                    {{ $t(node.label) }} {{ node.value }}
                </text>
            </g>
        </svg>

        <!-- 設備斷線時節點已因數值為空而全部轉灰，此處只補一行文字說明原因，
             否則一張全灰的圖看起來像是網頁壞了 -->
        <p v-if="offline" class="offline-note">
            {{ offlineText }}
        </p>
    </div>
</template>

<script>
// 三到四個節點的流向圖用 SVG 手寫即可，不引入第二套圖表函式庫
// （現有圖表全在 chart.js，混用會產生兩種視覺語言）。
//
// 版面刻意做成寬扁形（900×340），因為卡片的圖表區就是寬扁的；
// 若用接近正方形的 viewBox，preserveAspectRatio 會依高度縮放，
// 左右留下大片空白、圖形變得極小。

// 兩套版面：桌機卡片的圖表區是寬扁的（約 6:1），手機卡片接近 2:1。
// 用同一組座標會讓其中一邊被 preserveAspectRatio 縮到看不清字。
const LAYOUT = {
    wide: {
        viewBox: '0 0 900 215',
        radius: 26,
        bus: { y: 95, left: 340, right: 590 },
        tap: { pv: 340, grid: 420, battery: 500, load: 590 },
        node: {
            pv: { x: 110, y: 40 },
            grid: { x: 110, y: 150 },
            battery: { x: 500, y: 158 },
            load: { x: 800, y: 95 },
        },
    },
    // 手機卡片的圖表區約 330×160，故比例壓在 2:1 附近，
    // 讓 SVG 依寬度縮放後剛好吃滿高度，字級才不會被縮到看不清
    compact: {
        // 左右各留約 90 單位，否則最外側節點的文字標籤會被裁掉
        viewBox: '0 0 490 215',
        radius: 26,
        bus: { y: 110, left: 170, right: 320 },
        tap: { pv: 170, grid: 220, battery: 270, load: 320 },
        node: {
            pv: { x: 95, y: 38 },
            grid: { x: 95, y: 150 },
            battery: { x: 270, y: 155 },
            load: { x: 395, y: 110 },
        },
    },
}

const COLOR = {
    pv: '#D18800', // 與太陽能卡一致
    grid: '#005088', // 網站主色
    load: '#11caa0', // 頁籤 active bar 同色
    battery: '#7c5cbf',
    idle: '#c4c9cf',
}

export default {
    name: 'EnergyFlowChart',
    props: {
        cardSize: { type: String, default: 'large' },
        // 預設為 null 而非 0：沒有讀到值時顯示「—」，
        // 顯示 0 W 會被讀成「量到了，而且是零」。
        pv: { type: Number, default: null },
        grid: { type: Number, default: null },
        load: { type: Number, default: null },
        soc: { type: Number, default: null },
        charging: { type: Number, default: null },
        discharging: { type: Number, default: null },
        // 設備通訊中斷，畫面上的零值不代表真的是零
        offline: { type: Boolean, default: false },
        // 資料視窗內最後一筆有讀到資料的時間；中斷已久時為 null
        lastOnline: { type: Date, default: null },
    },
    computed: {
        // 找得到最後更新時間就一併標出，讓讀者判斷中斷多久了；
        // 中斷超過資料視窗長度時視窗內一筆都沒有，只說中斷
        offlineText() {
            if (!this.lastOnline) return this.$t('energy-flow.offline')
            const pad = (n) => n.toString().padStart(2, '0')
            const t = this.lastOnline
            const stamp = `${pad(t.getMonth() + 1)}/${pad(t.getDate())} ${pad(t.getHours())}:${pad(
                t.getMinutes()
            )}`
            return this.$t('energy-flow.offline-since', { time: stamp })
        },
        layout() {
            return this.cardSize === 'mobile' ? LAYOUT.compact : LAYOUT.wide
        },
        NODE_RADIUS() {
            return this.layout.radius
        },
        BUS() {
            return this.layout.bus
        },
        // 兩欄位同時有值時以充電為主（依 PM 對照表備註）
        batteryState() {
            if (this.charging > 0) return 'charging'
            if (this.discharging > 0) return 'discharging'
            return 'idle'
        },
        batteryPower() {
            if (this.batteryState === 'charging') return this.charging
            if (this.batteryState === 'discharging') return this.discharging
            return 0
        },
        nodes() {
            const NODE = this.layout.node
            return [
                {
                    id: 'pv',
                    ...NODE.pv,
                    icon: '☀',
                    label: 'energy-flow.pv',
                    value: this.formatPower(this.pv),
                    color: this.pv > 0 ? COLOR.pv : COLOR.idle,
                    state: this.pv > 0 ? 'active' : 'idle',
                },
                {
                    id: 'grid',
                    ...NODE.grid,
                    icon: '⚡',
                    label: 'energy-flow.grid',
                    value: this.formatPower(this.grid),
                    color: this.grid > 0 ? COLOR.grid : COLOR.idle,
                    state: this.grid > 0 ? 'active' : 'idle',
                },
                {
                    id: 'battery',
                    ...NODE.battery,
                    icon: '▮',
                    label: 'energy-flow.battery',
                    value: this.formatBattery(),
                    color: this.batteryState === 'idle' ? COLOR.idle : COLOR.battery,
                    state: this.batteryState === 'idle' ? 'idle' : 'active',
                },
                {
                    id: 'load',
                    ...NODE.load,
                    icon: '⌂',
                    label: 'energy-flow.load',
                    value: this.formatPower(this.load),
                    color: this.load > 0 ? COLOR.load : COLOR.idle,
                    state: this.load > 0 ? 'active' : 'idle',
                },
            ]
        },
        links() {
            const NODE = this.layout.node
            const TAP = this.layout.tap
            return [
                // 來源在左側，先水平走到自己的接點再垂直接上匯流排
                this.elbow('pv', NODE.pv, TAP.pv, this.pv, COLOR.pv, 'in'),
                this.elbow('grid', NODE.grid, TAP.grid, this.grid, COLOR.grid, 'in'),
                // 電池垂直吊在匯流排下方；充電時能量流向電池，動畫方向相反
                this.vertical(
                    'battery',
                    NODE.battery,
                    this.batteryPower,
                    COLOR.battery,
                    this.batteryState === 'charging' ? 'out' : 'in'
                ),
                // 負載在右側，水平接出
                this.horizontal('load', TAP.load, NODE.load, this.load, COLOR.load, 'out'),
            ]
        },
    },
    methods: {
        elbow(id, node, tapX, power, color, direction) {
            return this.link(
                id,
                `M ${node.x} ${node.y} L ${tapX} ${node.y} L ${tapX} ${this.BUS.y}`,
                power,
                color,
                direction
            )
        },
        vertical(id, node, power, color, direction) {
            return this.link(
                id,
                `M ${node.x} ${node.y} L ${node.x} ${this.BUS.y}`,
                power,
                color,
                direction
            )
        },
        horizontal(id, tapX, node, power, color, direction) {
            return this.link(
                id,
                `M ${tapX} ${this.BUS.y} L ${node.x} ${node.y}`,
                power,
                color,
                direction
            )
        },
        link(id, d, power, color, direction) {
            const active = power > 0
            return {
                id,
                d,
                color: active ? color : COLOR.idle,
                // 線寬隨功率變化，設上下限避免極端值破版。
                // 級距以 W 為基準：約 3600 W 時達到最粗
                width: active ? Math.min(11, 3 + power / 450) : 2.5,
                state: active ? `active flow-${direction}` : 'idle',
            }
        },
        // 瞬時功率的原始單位是 W，直接以 W 呈現，不做換算，
        // 避免與廠商端的數值對不起來
        formatPower(value) {
            if (value === null || value === undefined || Number.isNaN(value)) return '—'
            return `${Math.round(Number(value))} W`
        },
        formatBattery() {
            // 沒有讀到電量時不能說「未作用」——那是一種狀態判斷，
            // 而我們此刻並不知道電池在做什麼，只知道沒有資料。
            if (this.soc === null || this.soc === undefined) return '—'
            const soc = `${this.soc}%`
            if (this.batteryState === 'idle') return `${soc}・${this.$t('energy-flow.idle')}`
            const label =
                this.batteryState === 'charging'
                    ? this.$t('energy-flow.charging')
                    : this.$t('energy-flow.discharging')
            return `${soc}・${label} ${this.formatPower(this.batteryPower)}`
        },
    },
}
</script>

<style lang="scss" scoped>
.energy-flow {
    width: 100%;
    height: 100%;
    display: flex;
    // 直式排列，斷線說明才不會與 SVG 並排把圖擠扁；
    // 沒有說明時 svg 仍以 flex: 1 吃滿整個高度，與原本行為相同
    flex-direction: column;
    justify-content: center;
    align-items: center;

    svg {
        width: 100%;
        flex: 1 1 auto;
        min-height: 0;
    }
}

.offline-note {
    flex: 0 0 auto;
    margin: 0;
    padding: 2px 0 4px;
    font-size: 13px;
    line-height: 1.4;
    color: #8a6d3b;
    text-align: center;
}

.mobile .offline-note {
    font-size: 12px;
}

.bus-bar {
    fill: none;
    stroke: #8aaba1;
    stroke-width: 5;
    stroke-linecap: round;
}

.link {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;

    &.idle {
        stroke-dasharray: 8 8;
        opacity: 0.55;
    }

    &.active {
        stroke-dasharray: 12 10;
        animation: flow 1.4s linear infinite;
    }

    /* 流向朝匯流排與背離匯流排，動畫方向相反 */
    &.flow-in {
        animation-direction: normal;
    }
    &.flow-out {
        animation-direction: reverse;
    }
}

@keyframes flow {
    from {
        stroke-dashoffset: 44;
    }
    to {
        stroke-dashoffset: 0;
    }
}

.node {
    circle {
        transition: fill 0.3s;
    }
    &.idle {
        opacity: 0.6;
    }
}

.node-icon {
    text-anchor: middle;
    font-size: 24px;
    fill: #fff;
}

.node-label {
    text-anchor: middle;
    font-size: 19px;
    font-weight: bold;
    fill: #4a5568;
}

/* 緊湊版面的 viewBox 只有寬版的一半寬，字級需等比放大才維持相同視覺大小 */
.mobile {
    .node-icon {
        font-size: 26px;
    }
    .node-label {
        font-size: 22px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .link.active {
        animation: none;
    }
}
</style>
